// Utilities in this file are used in Component UX signature moment

/**
 * This function selects alpha-augmented (color with transparency value) theme or default color.
 * If theme color is valid as per the implemented logic, it's preferred over the default color.
 *
 * Function is designed for use in signature moment animation, but could be improved and reused in other cases where
 * inline application of transparency is cumbersome
 *
 * @param defaultColor should be an rgb or HEX color value, without opacities
 * @param themeSlot color value from a theme slot
 * @param transparency number value in the range (0, 1)
 * @returns an alpha augmented rgb or rrggbb
 */
export const get_RGBA_or_RRGGBBAA_ColorFromThemeSlot = (
  defaultColor: string,
  themeSlot?: string,
  transparency?: number
): string => {
  let color = defaultColor;

  if (color.startsWith('var') && themeSlot && themeSlot.startsWith('var(')) {
    // TODO: Resolve colors from css variables
    return '';
  }

  if (themeSlot && !themeSlot.startsWith('var(')) {
    color = themeSlot;
  }

  if (color.startsWith('#')) {
    if (color.length === 9) {
      // don't apply default or requested transparency. assume color is already valid rrggbbaa
      return color;
    }

    return `${color}${parseRgbAlphaValue(transparency)}`;
  }

  if (color.startsWith('rgb')) {
    if (color.startsWith('rgba')) {
      // don't apply default or requested transparency. assume color is already valid rgba
      return color;
    }

    const defaultRgbValues = [0, 0, 0];
    // retrieve r, g and b values from rgb color string
    const [r, g, b] = color.slice(color.indexOf('(') + 1, color.indexOf(')')).split(',') || defaultRgbValues;
    return `rgba(${r},${g},${b},${transparency || 0})`;
  }

  return defaultColor;
};

/**
 * Parses RGB alpha value to Hexadecimal alpha value (AA),
 * This function currently generalizes alpha translation, producing one of three transparency values:
 *
 * 00 - 0% transparency     - Between 0.75 and 1 alpha transparency range
 * 80 - 50% transparency    - Between 0.25 and 0.75 alpha transparency range
 * FF - 100% transparency - Between 0 and 0.25 alpha transparency range
 */
const parseRgbAlphaValue = (transparency: number = 0): string => {
  // rgb alpha in range (0.75, 1) -> return 100% AA transparency
  if (transparency >= 0.75) {
    return 'FF';
  }

  // rgb alpha in range (0.25, 0.75] -> return 50% AA transparency
  if (transparency >= 0.25) {
    return '80';
  }

  // rgb alpha in range (0, 0.25] -> return 0% AA transparency
  return '00';
};

// cspell:ignore RRGGBB RRGGBBAA
